/**
 * Terms of Service Page
 *
 * Displays the Terms of Service for Arber.
 * Last Updated: December 16, 2025
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPage.css';

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      {/* Navigation Header */}
      <nav className="legal-nav">
        <div className="legal-nav-content">
          <Link to="/" className="legal-brand">
            <img src="/logo.png" alt="Arber Logo" className="legal-logo" />
            <span className="legal-brand-text">Arber</span>
          </Link>
          <div className="legal-nav-links">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="legal-content">
        <div className="legal-container">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-updated">Last Updated: December 16, 2025</p>

          <section className="legal-section">
            <h2>1. Introduction and Acceptance of Terms</h2>
            <p>
              Welcome to <strong>Arber</strong> ("we," "us," or "our"). By accessing or using our website
              located at <a href="https://arber.bet" target="_blank" rel="noopener noreferrer">https://arber.bet</a> (the
              "Site") and our web-based sports betting analytics platform (the "Service"), you ("User" or "you")
              agree to be bound by these Terms of Service ("Terms").
            </p>
            <p className="legal-highlight">
              <strong>PLEASE READ THESE TERMS CAREFULLY.</strong> These Terms constitute a legally binding
              agreement between you and Arber. If you do not agree to these Terms, you must not access or
              use the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Eligibility</h2>
            <p>To use Arber, you represent and warrant that:</p>
            <ol>
              <li>You are at least <strong>21 years of age</strong> (or the legal gambling age in your jurisdiction, whichever is higher).</li>
              <li>Online sports betting is legal in your jurisdiction of residence.</li>
              <li>You have the legal power and authority to enter into these Terms.</li>
              <li>You are not barred from using the Service under the laws of the United States, the United Kingdom, the European Union, Australia, or other applicable jurisdictions.</li>
            </ol>
          </section>

          <section className="legal-section legal-critical">
            <h2>3. CRITICAL DISCLAIMER: NO GAMBLING SERVICES</h2>
            <p className="legal-highlight">
              <strong>Arber is strictly an analytics and information tool.</strong>
            </p>

            <h3>3.1 Informational Use Only</h3>
            <p>
              We provide data regarding odds, arbitrage opportunities, and expected value (+EV) calculations.
              We <strong>do not</strong> accept bets, handle funds, facilitate gambling transactions, or act
              as a sportsbook or bookmaker.
            </p>

            <h3>3.2 No Guarantee of Profit</h3>
            <p>
              While our algorithms identify statistical opportunities, sports betting involves inherent risk.
              <strong> Past performance, arbitrage calculations, and +EV metrics do not guarantee future results.</strong> Odds
              may change between the time they are displayed on Arber and the time you attempt to place a bet.
            </p>

            <h3>3.3 User Responsibility</h3>
            <p>
              You are solely responsible for any bets you place with third-party sportsbooks. Arber is not
              liable for any financial losses you may incur. You acknowledge that you may lose some or all
              of the money you wager.
            </p>

            <h3>3.4 Bookmaker Terms</h3>
            <p>
              Using arbitrage betting strategies may violate the Terms of Service of certain third-party
              bookmakers. Arber is not responsible if your accounts with third-party bookmakers are limited,
              suspended, banned, or if your winnings are voided by those bookmakers.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Account Registration and Security</h2>

            <h3>4.1 Account Creation</h3>
            <p>
              To access features, you must register for an account. You agree to provide accurate, current,
              and complete information (specifically your email address).
            </p>

            <h3>4.2 Security</h3>
            <p>
              You are responsible for maintaining the confidentiality of your login credentials. You accept
              responsibility for all activities that occur under your account.
            </p>

            <h3>4.3 No Sharing</h3>
            <p>
              Sharing your account credentials with others to circumvent subscription limits is strictly
              prohibited and may result in immediate account termination.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Subscription Services and Billing</h2>

            <h3>5.1 Subscription Tiers</h3>
            <p>
              Arber offers Free, Gold, and Platinum tiers. The specific features, limits (e.g., number of
              searches), and pricing for each tier are described on our pricing page and are incorporated
              into these Terms.
            </p>

            <h3>5.2 Billing</h3>
            <p>
              Subscriptions are billed in advance on a daily, monthly, or yearly basis, depending on the
              plan selected. Payments are processed securely via <strong>Stripe</strong>. We do not store
              your full credit card details.
            </p>

            <h3>5.3 Auto-Renewal</h3>
            <p className="legal-highlight">
              <strong>Your subscription will automatically renew</strong> at the end of each billing cycle
              unless you cancel it. You authorize us to charge your payment method for the renewal term.
            </p>

            <h3>5.4 Cancellation</h3>
            <p>
              You may cancel your subscription at any time via the Stripe Customer Portal accessible through
              your account settings. Cancellation takes effect at the end of the current billing period. You
              will continue to have access to paid features until the billing period ends.
            </p>

            <h3>5.5 No Refunds</h3>
            <p>
              Payments are non-refundable. There are no refunds or credits for partially used billing periods.
            </p>

            <h3>5.6 Price Changes</h3>
            <p>
              We reserve the right to change subscription fees upon 30 days' notice. Continued use of the
              Service after the price change constitutes your agreement to pay the modified amount.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Acceptable Use Policy</h2>
            <p>You agree not to:</p>
            <ol>
              <li>Use the Service for any illegal purpose or in violation of any local, state, national, or international law.</li>
              <li>Use any robot, spider, scraper, or other automated means to access the Service for any purpose (including data scraping).</li>
              <li>Resell, redistribute, or sub-license the data provided by Arber.</li>
              <li>Attempt to circumvent any rate limits (e.g., 1,000 requests per 15 minutes) or security measures.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
            </ol>
          </section>

          <section className="legal-section">
            <h2>7. Intellectual Property</h2>
            <p>
              The Service, including its "Arber" name, logo, source code, database structures, algorithms,
              and analysis, is the exclusive property of Arber. You are granted a limited, non-exclusive,
              non-transferable license to use the Service for your personal use only.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Third-Party Services and Links</h2>
            <p>
              The Service contains data from and links to third-party websites (Sportsbooks). We do not
              control these websites and are not responsible for their content, privacy policies, or
              practices. The inclusion of any odds data does not imply endorsement of the bookmaker by Arber.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Limitation of Liability</h2>
            <p className="legal-highlight">
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
            </p>

            <h3>9.1 "As Is" Basis</h3>
            <p>
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. We disclaim all warranties,
              whether express or implied, including accuracy of data, uptime, or fitness for a particular purpose.
            </p>

            <h3>9.2 No Liability for Damages</h3>
            <p>
              In no event shall Arber, its owners, or affiliates be liable for any indirect, incidental,
              special, consequential, or punitive damages, including without limitation, loss of profits,
              data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of
              or inability to access or use the Service; (ii) any conduct or content of any third party on
              the Service; or (iii) unauthorized access, use, or alteration of your transmissions or content.
            </p>

            <h3>9.3 Liability Cap</h3>
            <p>
              Arber's total liability to you for any claims arising out of or relating to these terms or
              your use of the service is limited to the amount you have paid us in the past twelve (12) months.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Arber and its affiliates from and against
              any claims, liabilities, damages, losses, and expenses, including reasonable legal fees,
              arising out of or in any way connected with your access to or use of the Service or your
              violation of these Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Termination</h2>
            <p>
              We may terminate or suspend your account and bar access to the Service immediately, without
              prior notice or liability, under our sole discretion, for any reason whatsoever and without
              limitation, including but not limited to a breach of the Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Governing Law</h2>
            <p>
              These Terms shall be governed and construed in accordance with the laws of the <strong>State
              of Delaware, United States</strong>, without regard to its conflict of law provisions. Any
              dispute arising from these Terms shall be resolved in the state or federal courts located
              in Delaware.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. If a revision is material, we will
              provide at least 30 days' notice prior to any new terms taking effect. What constitutes a
              material change will be determined at our sole discretion.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:arberodds@gmail.com">arberodds@gmail.com</a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="legal-footer">
        <div className="legal-footer-content">
          <p>&copy; 2025 Arber. All rights reserved.</p>
          <div className="legal-footer-links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <a href="mailto:arberodds@gmail.com">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
